import { AfterViewInit, Component, ElementRef, OnDestroy, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import { ButtonModule } from 'primeng/button';
import { Subscription, take } from 'rxjs';
import * as CookieConsent from 'vanilla-cookieconsent';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../services/auth/auth.service';

@Component({
  selector: 'cookie-consent-component',
  standalone: true,
  imports: [TranslocoModule, ButtonModule],
  templateUrl: './cookie-consent.components.html',
})
export class CookieConsentComponent implements AfterViewInit, OnDestroy {
  platform: string;
  isTextDescriptionAvailable = true;

  constructor(
    private readonly translocoService: TranslocoService,
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly elRef: ElementRef,
    private readonly renderer: Renderer2,
  ) {
    this.platform = Capacitor.getPlatform();
    this.authService.isLoggedIn$.pipe(take(1)).subscribe((loggedIn) => {
      if (this.platform === 'ios') {
        this.isTextDescriptionAvailable = false;
      }

      if (this.platform === 'android' && !loggedIn) {
        this.isTextDescriptionAvailable = false;
      }
    });
  }
  private langSub: Subscription | undefined;

  private initCookiesContent() {
    CookieConsent.run({
      onFirstConsent: () => {
        this.cookieConsentLogConsent();

        if (CookieConsent.acceptedCategory('ads')) {
          this.loadGoogleAds();
        }
      },

      onChange: ({ changedCategories, changedServices }) => {
        this.cookieConsentLogConsent();
        if (changedCategories.includes('ads')) {
          if (CookieConsent.acceptedCategory('ads')) {
            this.loadGoogleAds();
          } else {
            this.removeGoogleAds();
          }
        }
      },

      categories: {
        necessary: {
          enabled: true,
          readOnly: true,
        },
        analytics: {},
        ads: {
          autoClear: {
            cookies: [{ name: '_Secure-ENID' }],
          },
        },
      },

      language: {
        default: this.translocoService.getActiveLang(),
        translations: {
          en: {
            consentModal: {
              title: 'We use cookies',
              description:
                'We use cookies to ensure the proper functioning of the website, analyze traffic, and improve your online experience. By continuing to browse, you accept the use of cookies in accordance with our privacy policy.',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              showPreferencesBtn: 'Manage individual preferences',
              footer: '\n<a id="privacyPolicyLink" >Privacy Policy</a>\n',
            },
            preferencesModal: {
              title: 'Manage cookie preferences',
              acceptAllBtn: 'Accept all',
              acceptNecessaryBtn: 'Reject all',
              savePreferencesBtn: 'Accept current selection',
              closeIconLabel: 'Close modal',
              serviceCounterLabel: 'Service|Services',
              sections: [
                {
                  title: 'Your privacy choices',
                  description:
                    "In this panel you can set some preferences regarding the processing of your personal data. You can review and modify your choices at any time by reopening this panel using the provided link. To deny consent to the specific processing activities described below, toggle the switches off or use the 'Reject all' button and confirm that you want to save your choices.",
                },
                {
                  title: 'Strictly necessary',
                  description:
                    'These cookies are essential for the proper functioning of the website and cannot be disabled.',
                  linkedCategory: 'necessary',
                  cookieTable: {
                    caption: 'Necessary Cookies Table',
                    headers: {
                      name: 'Cookie',
                      domain: 'Domain',
                      desc: 'Description',
                    },
                    body: [
                      {
                        name: 'ACCOUNT_CHOOSER',
                        domain: 'google.com',
                        desc: 'Stores last used Google account (multi-account chooser).',
                      },
                      {
                        name: 'APISID',
                        domain: 'google.com',
                        desc: 'Cookie used for user preferences/security (Google).',
                      },
                      {
                        name: 'HSID',
                        domain: 'google.com',
                        desc: 'Security cookie for login/authentication.',
                      },
                      {
                        name: 'LSID',
                        domain: 'google.com',
                        desc: 'Security/session cookie from Google.',
                      },
                      {
                        name: 'SAPISID',
                        domain: 'google.com',
                        desc: 'Cookie used for user preferences/security (Google).',
                      },
                      {
                        name: 'SID',
                        domain: 'google.com',
                        desc: 'Security cookie for Google session.',
                      },
                      {
                        name: 'SIDCC',
                        domain: 'google.com',
                        desc: 'Security cookie protecting user data from unauthorized access.',
                      },
                      {
                        name: 'SSID',
                        domain: 'google.com',
                        desc: 'Security cookie for Google session.',
                      },
                      {
                        name: '_Host-1PSID',
                        domain: 'google.com',
                        desc: 'Host-only session cookie for extra Google account security.',
                      },
                      {
                        name: '_Host-3PSID',
                        domain: 'google.com',
                        desc: 'Similar security cookie for Google within a 3P context.',
                      },
                      {
                        name: '_Host-GAPS',
                        domain: 'google.com',
                        desc: 'Used for additional account security/protection.',
                      },
                      {
                        name: '_Secure-1PAPISID',
                        domain: 'google.com',
                        desc: 'Secure cookie for Google authentication/anti-fraud.',
                      },
                      {
                        name: '_Secure-1PSID',
                        domain: 'google.com',
                        desc: 'Secure cookie for Google session/authentication.',
                      },
                      {
                        name: '_Secure-3PAPISID',
                        domain: 'google.com',
                        desc: 'Cookie used for security (and possibly ads) with Google.',
                      },
                      {
                        name: '_Secure-3PSID',
                        domain: 'google.com',
                        desc: 'Used for security or advertising in Google environment.',
                      },
                      {
                        name: '_Secure-1PSIDCC',
                        domain: 'google.com',
                        desc: 'Secure cookie for account security (consent-based).',
                      },
                      {
                        name: '_Secure-3PSIDCC',
                        domain: 'google.com',
                        desc: 'Used for ad personalization / security checks.',
                      },
                      {
                        name: 'translocoLang',
                        domain: 'apprezzoo.it',
                        desc: 'Cookie used for storing the user’s chosen language.',
                      },
                      {
                        name: 'g_state',
                        domain: 'apprezzoo.it',
                        desc: 'Cookie used to store local/UI states for the site.',
                      },
                    ],
                  },
                },
                {
                  title: 'Targeting and Advertising',
                  description:
                    'These cookies are used to make advertising messages more relevant to you and your interests. Their purpose is to display engaging and pertinent ads for the individual user, making them more valuable for publishers and third-party advertisers.',
                  linkedCategory: 'ads',
                  cookieTable: {
                    caption: 'Advertising Cookies Table',
                    headers: {
                      name: 'Cookie',
                      domain: 'Domain',
                      desc: 'Description',
                    },
                    body: [
                      {
                        name: '_Secure-ENID',
                        domain: 'google.com',
                        desc: 'Used by Google for personalized advertising and security checks.',
                      },
                    ],
                  },
                },
                {
                  title: 'More Information',
                  description:
                    'For any questions regarding our cookie policy and your choices, please <a (click)="">contact us</a>',
                },
              ],
            },
          },
          it: {
            consentModal: {
              title: 'Utilizziamo i cookie',
              description:
                "Utilizziamo i cookie per garantire il corretto funzionamento del sito, analizzare il traffico e migliorare la tua esperienza online. Continuando a navigare, accetti l'uso dei cookie in conformità alla nostra politica sulla privacy.",
              acceptAllBtn: 'Accetta tutto',
              acceptNecessaryBtn: 'Rifiuta tutto',
              showPreferencesBtn: 'Gestisci le preferenze individuali',
              footer: '\n<a id="privacyPolicyLink" >Privacy Policy</a>\n',
            },
            preferencesModal: {
              title: 'Gestisci le preferenze dei cookie',
              acceptAllBtn: 'Accetta tutto',
              acceptNecessaryBtn: 'Rifiuta tutto',
              savePreferencesBtn: 'Accetta la selezione corrente',
              closeIconLabel: 'Chiudi il modal',
              serviceCounterLabel: 'Servizio|Servizi',
              sections: [
                {
                  title: 'Le tue scelte sulla privacy',
                  description:
                    'In questo pannello puoi esprimere alcune preferenze relative al trattamento dei tuoi dati personali. Puoi rivedere e modificare le scelte in qualsiasi momento richiamando questo pannello tramite il link fornito. Per negare il consenso alle specifiche attività di trattamento descritte di seguito, disattiva gli interruttori o utilizza il pulsante “Rifiuta tutto” e conferma di voler salvare le tue scelte.',
                },
                {
                  title: 'Strettamente necessari',
                  description:
                    'Questi cookie sono essenziali per il corretto funzionamento del sito web e non possono essere disabilitati.',
                  linkedCategory: 'necessary',
                  cookieTable: {
                    caption: 'Tabella dei cookie strettamente necessari',
                    headers: {
                      name: 'Cookie',
                      domain: 'Dominio',
                      desc: 'Descrizione',
                    },
                    body: [
                      {
                        name: 'ACCOUNT_CHOOSER',
                        domain: 'google.com',
                        desc: "Memorizza l'account Google usato di recente (multi-account).",
                      },
                      {
                        name: 'APISID',
                        domain: 'google.com',
                        desc: 'Cookie per preferenze/sicurezza (Google).',
                      },
                      {
                        name: 'HSID',
                        domain: 'google.com',
                        desc: 'Cookie di sicurezza per login/autenticazione.',
                      },
                      {
                        name: 'LSID',
                        domain: 'google.com',
                        desc: 'Cookie di sessione/sicurezza di Google.',
                      },
                      {
                        name: 'SAPISID',
                        domain: 'google.com',
                        desc: 'Cookie per preferenze/sicurezza (Google).',
                      },
                      {
                        name: 'SID',
                        domain: 'google.com',
                        desc: 'Cookie di sicurezza per la sessione Google.',
                      },
                      {
                        name: 'SIDCC',
                        domain: 'google.com',
                        desc: 'Cookie di sicurezza per proteggere i dati da accessi non autorizzati.',
                      },
                      {
                        name: 'SSID',
                        domain: 'google.com',
                        desc: 'Cookie di sicurezza per la sessione Google.',
                      },
                      {
                        name: '_Host-1PSID',
                        domain: 'google.com',
                        desc: 'Cookie di sessione (host-only) per maggiore sicurezza account Google.',
                      },
                      {
                        name: '_Host-3PSID',
                        domain: 'google.com',
                        desc: 'Cookie di sicurezza Google in contesto di terze parti.',
                      },
                      {
                        name: '_Host-GAPS',
                        domain: 'google.com',
                        desc: 'Usato per ulteriore protezione dell’account Google.',
                      },
                      {
                        name: '_Secure-1PAPISID',
                        domain: 'google.com',
                        desc: 'Cookie sicuro per autenticazione Google/anti-frode.',
                      },
                      {
                        name: '_Secure-1PSID',
                        domain: 'google.com',
                        desc: 'Cookie sicuro per sessione/autenticazione Google.',
                      },
                      {
                        name: '_Secure-3PAPISID',
                        domain: 'google.com',
                        desc: 'Cookie usato per sicurezza e potenziali scopi pubblicitari Google.',
                      },
                      {
                        name: '_Secure-3PSID',
                        domain: 'google.com',
                        desc: 'Cookie per sicurezza o advertising in contesto Google.',
                      },
                      {
                        name: '_Secure-1PSIDCC',
                        domain: 'google.com',
                        desc: 'Cookie sicuro per protezione account (in base al consenso).',
                      },
                      {
                        name: '_Secure-3PSIDCC',
                        domain: 'google.com',
                        desc: 'Utilizzato per personalizzazione annunci e controlli di sicurezza.',
                      },
                      {
                        name: 'translocoLang',
                        domain: 'apprezzoo.it',
                        desc: 'Cookie usato per memorizzare la lingua scelta dall’utente.',
                      },
                      {
                        name: 'g_state',
                        domain: 'apprezzoo.it',
                        desc: 'Cookie usato per memorizzare stati UI/locali per il sito.',
                      },
                    ],
                  },
                },
                {
                  title: 'Targeting e Pubblicità',
                  description:
                    "Questi cookie vengono utilizzati per rendere i messaggi pubblicitari più pertinenti a te e ai tuoi interessi. L'intento è di mostrare annunci rilevanti e coinvolgenti per l'utente, rendendoli così più preziosi per editori e inserzionisti terzi.",
                  linkedCategory: 'ads',
                  cookieTable: {
                    caption: 'Tabella dei cookie pubblicitari',
                    headers: {
                      name: 'Cookie',
                      domain: 'Dominio',
                      desc: 'Descrizione',
                    },
                    body: [
                      {
                        name: '_Secure-ENID',
                        domain: 'google.com',
                        desc: 'Usato da Google per annunci personalizzati e controlli di sicurezza.',
                      },
                    ],
                  },
                },
                {
                  title: 'Maggiori informazioni',
                  description:
                    'Per qualsiasi domanda relativa alla politica sui cookie e alle tue scelte, per favore <a>contattaci</a>.',
                },
              ],
            },
          },
        },
      },
    });
    setTimeout(() => {
      const privacyLink = this.elRef.nativeElement.querySelector('#privacyPolicyLink');

      if (privacyLink) {
        this.renderer.listen(privacyLink, 'click', () => this.goToPrivacy());
      }
    }, 500);
  }
  private removeGoogleAds(): void {
    const adsScript = document.querySelector(
      'script[src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]',
    );
    if (adsScript) {
      adsScript.remove();
    }

    if ((window as any).adsbygoogle) {
      delete (window as any).adsbygoogle;
    }

    try {
      CookieConsent.eraseCookies('_Secure-ENID');
    } catch (err) {
      console.error(err);
    }
    this.removeOriginTrialMetaTags();
  }
  private removeOriginTrialMetaTags(): void {
    const originTrialMetaTags = document.querySelectorAll('meta[http-equiv="origin-trial"]');
    originTrialMetaTags.forEach((meta) => {
      meta.remove();
    });
  }
  loadGoogleAds() {
    const script = document.createElement('script');
    script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
    script.async = true;
    document.head.appendChild(script);
  }
  openModalConsent() {
    CookieConsent.showPreferences();
  }
  cookieConsentLogConsent() {
    const cookie = CookieConsent.getCookie();
    const preferences = CookieConsent.getUserPreferences();

    this.logConsent(
      cookie.consentId,
      preferences.acceptType,
      preferences.acceptedCategories,
      preferences.rejectedCategories,
      cookie.consentTimestamp,
    );
  }

  logConsent(
    uuid: string,
    acceptType: string,
    acceptedCategories: string[],
    rejectedCategories: string[],
    consentTimestamp: string,
  ) {
    let id: number | null;
    this.authService.currentUser$.pipe(take(1)).subscribe((resp) => {
      if (resp) {
        id = resp.id;
      }

      const userConsent = {
        userId: id,
        consentId: uuid,
        acceptType: acceptType,
        acceptedCategories: acceptedCategories,
        rejectedCategories: rejectedCategories,
        consentTimestamp: consentTimestamp,
      };
      const originalUrl = environment.shopApiUrl;
      const newUrl = originalUrl.replace('/shop-api', '');

      fetch(newUrl + '/consent-logging', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userConsent),
      });
    });
  }
  goToPrivacy() {
    CookieConsent.hide();
    this.router.navigate(['/privacy-policy']);
  }
  ngOnDestroy(): void {
    if (this.langSub) {
      this.langSub.unsubscribe();
    }
  }
  ngAfterViewInit() {
    if (this.platform !== 'ios') {
      this.initCookiesContent();

      this.langSub = this.translocoService.langChanges$.subscribe((newLang) => {
        CookieConsent.setLanguage(newLang);
      });

      const observer = new MutationObserver(() => {
        const privacyLink = document.querySelector('#privacyPolicyLink'); // Controlliamo direttamente nel documento

        if (privacyLink) {
          this.renderer.listen(privacyLink, 'click', () => this.goToPrivacy());
          observer.disconnect(); // Interrompiamo l'osservazione una volta trovato il link
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    } else {
      this.manageIOSPermission();
    }
  }

  public async manageIOSPermission(): Promise<void> {
    let status = await this.getStatus();

    if (status === AppTrackingStatus.NOT_DETERMINED) {
      status = await this.requestPermission();
    }

    let acceptedType = 'all';
    let acceptedCategories = ['necessary', 'analytics', 'ads'];
    let rejectedCategories: string[] = [];
    const consentTimestamp = Date.now();

    if (status === AppTrackingStatus.AUTHORIZED) {
      this.loadGoogleAds();
    } else if (status === AppTrackingStatus.DENIED || status === AppTrackingStatus.RESTRICTED) {
      acceptedType = 'necessary';
      acceptedCategories = ['necessary'];
      rejectedCategories = ['analytics', 'ads'];
      this.removeGoogleAds();
    }

    const consentId = self.crypto.randomUUID();

    CookieConsent.setCookieData({
      value: { consentId, consentTimestamp, categories: acceptedCategories },
    });

    this.logConsent(
      consentId,
      acceptedType,
      acceptedCategories,
      rejectedCategories,
      new Date(consentTimestamp).toISOString(),
    );
  }

  public async getStatus(): Promise<AppTrackingStatus> {
    const response = await AppTrackingTransparency.getStatus();

    return response.status as unknown as AppTrackingStatus;
  }

  public async requestPermission(): Promise<AppTrackingStatus> {
    const response = await AppTrackingTransparency.requestPermission();

    return response.status as unknown as AppTrackingStatus;
  }
}

enum AppTrackingStatus {
  AUTHORIZED = 'authorized',
  DENIED = 'denied',
  NOT_DETERMINED = 'notDetermined',
  RESTRICTED = 'restricted',
}
